import React, { useCallback } from 'react';
import { AppButton,Box, Inscription, Preloader, ShimmerBox } from '@common-fe/common-fe';

import { WarnModal } from '@/components/elements';
import useEmployerTerminationBanner from '@/modules/core/hooks/useEmployerTerminationBanner';

import { TerminationSteps } from '../store/usePlanTerminationModal.store';

import { useCancelPlanTermination } from './hooks/useCancelPlanTermination';
import { usePlanTermination } from './hooks/usePlanTermination';
import { Mode, PlanTerminationCoverageDatesForm } from './PlanTerminationCoverageDatesForm';
import { PlanTerminationDateForm } from './PlanTerminationDateForm';
import { PlanTerminationHsaTransferForm } from './PlanTerminationHsaTransferForm';

const INVALID_DATE_RANGE_TEXT = 'Termination date must be within the current plan dates and no more than 6 months in advance.';
const INVALID_PLAN_COVERAGE_DATE_TEXT = 'Coverage end date and last date to submit claims cannot be before plan started';
const CLAIM_LAST_DATE_ERROR_TEXT = 'Last date to submit claims cannot be before coverage end date';
const PLAN_TERMINATION_IS_AFTER_ER_TERMINATION_TEXT = 'Plan termination date cannot be after employer termination date';

interface Props {
  onCancel?: (value: boolean) => void;
}

const PlanTerminationModalForm: React.FC<Props> = ({ onCancel }) => {
  const {
    isSubmit,
    isServerError,
    setIsServerError,
    handleSubmit,
    handleCancel,
    changeValues,
    coverageData,
    isLoading,
    step,
    modalDescription,
    isHsaAccount,
    setDateIsFilled,
    setHsaIsFilled,
    currentButtonTitle,
    terminationDate,
    isThereTerminationDate,
    terminationLoading,
    editMode,
    hsaTransferOfAssets,
    // planData,
    minDate,
    isTerminationDateError,
    isClaimLastDateError,
    isEndOfCoverageError,
    isLastDateToSubmitBeforeEndOfCoverageError,
    planId,
    planName,
    handleChangeVisibility,
    isTerminationAfterErTerminationError
  } = usePlanTermination({ onCancel });

  const { handleOpenCancelPlanTerminationModal } = useCancelPlanTermination();
    const { showBanner: isOrgTermination } = useEmployerTerminationBanner();

  const handleCancelTermination = useCallback(() => {
    handleChangeVisibility(false);
    handleOpenCancelPlanTerminationModal(
      planId,
      planName,
      isHsaAccount,
    );

  }, [handleChangeVisibility, handleOpenCancelPlanTerminationModal, isHsaAccount, planId, planName]);

  return (
    <>
      {isThereTerminationDate ? (
        <Box margin={{ bottom: 'spacing24' }}>
          <Inscription textAlign="center" size="14px">
            {modalDescription}
          </Inscription>
        </Box>
      ) : null}

      <Box data-testid="PlanTerminationModalForm_container">
        <Box direction="column">
          <Box round="container1Round" pad="spacing24" background="module">
            {(step === TerminationSteps.SET_TERMINATION_PARAMS && isLoading) ? (
              <ShimmerBox height="400px" width="100%"/>
            ) : (
              <Box gap="spacing24">
                {step === TerminationSteps.SET_TERMINATION_DATE ? (
                  <PlanTerminationDateForm
                    handleChangeValues={changeValues}
                    isSubmit={isSubmit}
                    setSetup={setDateIsFilled}
                    terminationDate={terminationDate}
                    minDate={minDate}
                    isExternalError={isTerminationDateError}
                  />
                ) : null}

                {/* {isLoading && editMode ? (
                  <Box width="100%">
                    <ShimmerBox
                      width="100%"
                      height="130px"
                    />
                  </Box>
                ) : null} */}

                {/* {(step === TerminationSteps.SET_TERMINATION_DATE && editMode && !isLoading) ? (
                  <PlanTerminationCoverageDatesForm mode={Mode.PLANS} planList={planData} />
                ) : null} */}

                {step === TerminationSteps.SET_TERMINATION_PARAMS ? (
                  <PlanTerminationCoverageDatesForm
                    mode={Mode.COVERAGES}
                    coveragesList={coverageData}
                  />
                ) : null}

                {(step === TerminationSteps.SET_TERMINATION_PARAMS && isHsaAccount) ? (
                  <PlanTerminationHsaTransferForm
                    handleChangeValues={changeValues}
                    isSubmit={isSubmit}
                    setSetup={setHsaIsFilled}
                    defaultHsaTransferOfAssets={hsaTransferOfAssets}
                  />
                ) : null}
              </Box>
            )}
          </Box>

          {isTerminationDateError ? (
            <Box margin={{ top: 'spacing24' }}>
              <Inscription
                size="14px"
                color="danger"
                textAlign="end"
                lineHeight='20px'
              >
                {INVALID_DATE_RANGE_TEXT}
              </Inscription>
            </Box>
          ) : null}
          {isTerminationAfterErTerminationError ? (
            <Box margin={{ top: 'spacing24' }}>
              <Inscription
                size="14px"
                color="danger"
                textAlign="end"
                lineHeight='20px'
              >
                {PLAN_TERMINATION_IS_AFTER_ER_TERMINATION_TEXT}
              </Inscription>
            </Box>
          ) : null}
          {(isClaimLastDateError || isEndOfCoverageError) && step === TerminationSteps.SET_TERMINATION_PARAMS ? (
            <Box margin={{ top: 'spacing24' }}>
              <Inscription
                size="14px"
                color="danger"
                textAlign="end"
                lineHeight='20px'
              >
                {INVALID_PLAN_COVERAGE_DATE_TEXT}
              </Inscription>
            </Box>
          ) : null}
          {isLastDateToSubmitBeforeEndOfCoverageError && step === TerminationSteps.SET_TERMINATION_PARAMS ? (
            <Box margin={{ top: 'spacing24' }}>
              <Inscription
                size="14px"
                color="danger"
                textAlign="end"
                lineHeight='20px'
              >
                {CLAIM_LAST_DATE_ERROR_TEXT}
              </Inscription>
            </Box>
          ) : null}

          <Box
            direction="row"
            fill="horizontal"
            margin={{ top: 'spacing24' }}
            align="end"
            justify={editMode ? 'between' : 'end'}
            width="medium"
            gap="xs"
          >
            {editMode ? (
              <Box>
                <AppButton
                  testId="PlanTerminationModalForm_cancel_termination"
                  buttonType="secondary"
                  width="control"
                  type="button"
                  onClick={handleCancelTermination}
                  color="red"
                  disabled={isOrgTermination}
                >
                  Cancel Termination
                </AppButton>
              </Box>
            ) : null}
            <Box direction="row" gap="xs">
              <AppButton
                testId="PlanTerminationModalForm_cancel"
                buttonType="secondary"
                width="control"
                type="button"
                onClick={handleCancel}
              >
                Cancel
              </AppButton>
              <AppButton
                testId="PlanTerminationModalForm_submit"
                type="button"
                disabled={terminationLoading}
                onClick={handleSubmit}
                width="control"
              >
                {terminationLoading ? (
                  <Preloader />
                ) : (
                  currentButtonTitle
                )}
              </AppButton>
            </Box>
          </Box>
        </Box>
      </Box>
      <WarnModal
        testId="plan-termination-server-error-modal"
        visible={isServerError}
        onSetVisible={setIsServerError}
        header="The server encountered an error processing the request"
        helptext={`It doesn't appear to have affected your data, but we cannot save the Plan termination with the latest records.
        Our technical staff have been automatically notified and will be looking into this with the utmost urgency.`}
        buttonText="Close"
        onSubmit={handleSubmit}
        submitButtonText="Try again"
      />
    </>
  );
};

export default PlanTerminationModalForm;
